import React, { useState, useEffect, useMemo, useCallback } from 'react';
// build-marker: searches-parity-v2
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from './shell/AppShell';
import BulkActionBar from './shell/BulkActionBar';
import FilterSelect from './shell/FilterSelect';
import OpportunityRolesSection from './OpportunityRolesSection';
import useSidebarOrder from '../hooks/useSidebarOrder';
import { exportSearchesToCSV } from '../utils/exportUtils';
import { API_BASE_URL } from '../config/api';
import { hasPermission } from '../utils/permissions';
import { useAssistantContext } from '../utils/assistantContext';
import EditSearchDrawer from './EditSearchDrawer';

const API = API_BASE_URL;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'closed', label: 'Closed', color: 'bg-gray-200 text-gray-600' },
  { value: 'placement', label: 'Placement', color: 'bg-blue-100 text-blue-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-600' },
  { value: 'internal_hire', label: 'Internal Hire', color: 'bg-purple-100 text-purple-700' },
  { value: 'draft', label: 'Draft', color: 'bg-amber-100 text-amber-700' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-500' },
];

const statusStyle = (status) => {
  const opt = STATUS_OPTIONS.find(o => o.value === status);
  return opt?.color || 'bg-gray-100 text-gray-500';
};

// Modern filter dropdown: keeps the familiar rounded box, shows a dimmed
// label + emphasized value + a custom chevron, and tints green when a
// non-default value is active. Options: [{ value, label }]. defaultValue
// marks the "unfiltered" state (no green tint when selected).
const SearchesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(searchParams.get('cat') || 'executive_search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [firmFilter, setFirmFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortDir(key === 'name' || key === 'firm_name' ? 'asc' : 'desc'); }
  };
  // Toggleable table columns (the name column is always shown).
  const COLUMNS = [
    { key: 'firm_name', label: 'Company' },
    { key: 'location', label: 'Location' },
    { key: 'card_count', label: 'Candidates' },
    { key: 'status', label: 'Status' },
    { key: 'created_by', label: 'Created By' },
    { key: 'assigned', label: 'Assigned' },
    { key: 'created_at', label: 'Created' },
  ];
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('searches_visible_cols') || 'null');
      if (saved) return saved;
    } catch {}
    return { firm_name: true, location: true, card_count: true, status: true, created_by: true, assigned: true, created_at: true };
  });
  const [showColMenu, setShowColMenu] = useState(false);
  const toggleCol = (key) => {
    setVisibleCols(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('searches_visible_cols', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  // View mode: 'table' (default, matches previous behavior) or 'cards'.
  // Persisted per user via localStorage so the preference sticks
  // across sessions. Doesn't apply to the Opportunities category —
  // OpportunityRolesSection owns its own layout.
  const VIEW_STORAGE_KEY = 'banyak:searches_view_mode';
  const [viewMode, setViewModeState] = useState(() => {
    try { return localStorage.getItem(VIEW_STORAGE_KEY) === 'cards' ? 'cards' : 'table'; }
    catch { return 'table'; }
  });
  const setViewMode = (mode) => {
    setViewModeState(mode);
    try { localStorage.setItem(VIEW_STORAGE_KEY, mode); } catch {}
  };

  // ── Bulk selection ──────────────────────────────────────────────
  // Set of pipeline IDs the user has checked. Persists across the
  // table↔cards toggle so nothing is lost when the view changes.
  // Cleared when filters change so users don't accidentally act on
  // rows that are no longer visible.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editPipeline, setEditPipeline] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const toggleWithShift = (id, index, list, shift) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (shift && lastSelectedIndex !== null && list) {
        // Shift-click: add the whole range (don't toggle, don't move anchor)
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        for (let i = start; i <= end; i++) {
          const rid = list[i]?.id;
          if (rid) next.add(rid);
        }
      } else {
        // Regular click: toggle single + set anchor
        if (next.has(id)) next.delete(id); else next.add(id);
        setLastSelectedIndex(index);
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = () => setSelectedIds(new Set(filtered.map(p => p.id)));

  const bulkDelete = async () => {
    if (selectedIds.size === 0 || bulkUpdating) return;
    const label = activeCategory === 'executive_search' ? 'search(es)' : 'project(s)';
    if (!window.confirm(`Permanently delete ${selectedIds.size} ${label}? This cannot be undone.`)) return;
    setBulkUpdating(true);
    const ids = Array.from(selectedIds);
    try {
      const results = await Promise.allSettled(
        ids.map(id => fetch(`${API}/api/pipelines/${id}`, { method: 'DELETE', headers })
          .then(r => { if (!r.ok) throw new Error(r.status); return r; }))
      );
      const okIds = ids.filter((_, i) => results[i].status === 'fulfilled');
      if (okIds.length > 0) setAllData(prev => prev.filter(p => !okIds.includes(p.id)));
    } catch (err) {
      console.error(err);
    } finally {
      setBulkUpdating(false);
      clearSelection();
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (!newStatus || selectedIds.size === 0 || bulkUpdating) return;
    setBulkUpdating(true);
    const ids = Array.from(selectedIds);
    const token = localStorage.getItem('authToken');
    try {
      const results = await Promise.allSettled(
        ids.map(id => fetch(`${API}/api/pipelines/${id}/status`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus }),
        }).then(r => { if (!r.ok) throw new Error(r.status); return r; }))
      );
      const okIds = ids.filter((_, i) => results[i].status === 'fulfilled');
      const failedIds = ids.filter((_, i) => results[i].status === 'rejected');
      // Update local state for the ones that succeeded
      if (okIds.length > 0) {
        setAllData(prev => prev.map(p =>
          okIds.includes(p.id) ? { ...p, status: newStatus } : p
        ));
      }
      if (failedIds.length > 0) {
        alert(`Updated ${okIds.length} of ${ids.length} searches. ${failedIds.length} failed — please retry those individually.`);
      }
      clearSelection();
    } catch (err) {
      alert('Bulk status change failed.');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Create project modal
  const [showCreate, setShowCreate] = useState(false);   // combined create sidebar
  const [createType, setCreateType] = useState(null);    // 'search' | 'project' | null (picker)
  const [newProject, setNewProject] = useState({ name: '', firm_id: '', description: '', location: '' });
  const [firms, setFirms] = useState([]);
  const [creating, setCreating] = useState(false);

  // Create executive search modal
  const [newSearch, setNewSearch] = useState({ name: '', client_firm_id: '', description: '', requirements: '', location: '', compensation_range: '' });

  // User assignment
  const [teamMembers, setTeamMembers] = useState([]);
  const [assignPopover, setAssignPopover] = useState(null); // pipeline id

  // Sidebar ordering (persisted per user)
  const [savedCategoryOrder, setSavedCategoryOrder] = useSidebarOrder('searches');

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/pipelines/all-searches`, { headers });
      if (res.ok) setAllData(await res.json());
    } catch (err) {
      console.error('Error loading searches:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFirms = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/firms`, { headers });
      if (res.ok) setFirms(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadData(); loadFirms(); loadTeam(); }, []);

  // Clear bulk selection whenever the visible list changes materially.
  // Prevents "phantom" selections where the user checks a row, changes
  // a filter, then unknowingly triggers a bulk action on rows they
  // can't see anymore.
  useEffect(() => {
    setSelectedIds(new Set());
    setLastSelectedIndex(null);
  }, [activeCategory, statusFilter, firmFilter, searchQuery]);

  const loadTeam = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/users/firm-members`, { headers });
      if (res.ok) setTeamMembers(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  // Split by category. "all" shows searches + projects (opportunities
  // are handled by their own section — see main render). "opportunities"
  // returns an empty list here since we render OpportunityRolesSection
  // instead of the searches table.
  const categoryData = useMemo(() => {
    if (activeCategory === 'opportunities') return [];
    if (activeCategory === 'all') return allData;
    return allData.filter(p => (p.category || 'executive_search') === activeCategory);
  }, [allData, activeCategory]);

  // Available firms for filter
  const availableFirms = useMemo(() => {
    const firmSet = new Map();
    categoryData.forEach(p => {
      if (p.firm_name && p.firm_id) firmSet.set(String(p.firm_id), p.firm_name);
    });
    return Array.from(firmSet.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [categoryData]);

  // Filtered + sorted
  const filtered = useMemo(() => {
    let list = categoryData;

    if (statusFilter !== 'all') {
      list = list.filter(p => p.status === statusFilter);
    }
    if (firmFilter !== 'all') {
      list = list.filter(p => String(p.firm_id) === firmFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.firm_name || '').toLowerCase().includes(q) ||
        (p.created_by_name || '').toLowerCase().includes(q) ||
        (p.location || '').toLowerCase().includes(q)
      );
    }

    list = [...list].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'name': return dir * (a.name || '').localeCompare(b.name || '');
        case 'firm_name': return dir * (a.firm_name || '').localeCompare(b.firm_name || '');
        case 'card_count': return dir * ((parseInt(a.card_count) || 0) - (parseInt(b.card_count) || 0));
        case 'created_at':
        default: return dir * (new Date(a.submitted_at || a.created_at || 0) - new Date(b.submitted_at || b.created_at || 0));
      }
    });

    return list;
  }, [categoryData, statusFilter, firmFilter, searchQuery, sortBy, sortDir]);

  // Counts per category
  const searchCount = useMemo(() => allData.filter(p => (p.category || 'executive_search') === 'executive_search').length, [allData]);
  const projectCount = useMemo(() => allData.filter(p => p.category === 'project').length, [allData]);

  const totalCandidates = useMemo(() =>
    filtered.reduce((sum, p) => sum + (parseInt(p.card_count) || 0), 0)
  , [filtered]);

  // Lock On: publish the visible searches so the assistant can analyze them.
  const assistantRows = useMemo(() => filtered.map(p => ({
    search_id: p.id,
    name: p.name,
    code: `SR-${String(p.id).padStart(4, '0')}`,
    firm: p.firm_name || null,
    location: p.location || null,
    status: p.status || null,
    category: p.category || 'executive_search',
    candidate_count: parseInt(p.card_count) || 0,
    owner: p.created_by_name || null,
  })), [filtered]);

  useAssistantContext({
    type: 'search_list',
    label: 'Searches',
    rows: assistantRows,
    columns: ['search_id', 'name', 'code', 'firm', 'location', 'status', 'category', 'candidate_count', 'owner'],
    totalCount: allData.length,
    meta: {
      filtered: filtered.length !== allData.length,
      status_filter: statusFilter && statusFilter !== 'all' ? statusFilter : null,
    },
  });

  const categoryMeta = {
    all: {
      title: 'All',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      ),
      count: allData.length,
    },
    executive_search: {
      title: 'Executive Searches',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      count: searchCount,
    },
    project: {
      title: 'Projects',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
      count: projectCount,
    },
    opportunities: {
      title: 'Opportunities',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      // Opportunities are managed by OpportunityRolesSection which
      // owns its own data. No count here — the pill just navigates.
      count: null,
    },
  };

  const sidebarItems = useMemo(() => {
    // Default order — all, executive_search, project, opportunities.
    // savedCategoryOrder may not include the new "all"/"opportunities"
    // if it was set before this update, so we merge intelligently.
    const defaults = ['all', 'executive_search', 'project', 'opportunities'];
    const saved = savedCategoryOrder || [];
    const order = [...saved.filter(k => defaults.includes(k))];
    defaults.forEach(k => { if (!order.includes(k)) order.push(k); });
    return order.map(cat => {
      const meta = categoryMeta[cat];
      if (!meta) return null;
      return {
        key: cat,
        label: null,
        title: meta.title,
        icon: meta.icon,
        badge: meta.count,
        active: activeCategory === cat,
        onClick: () => { setActiveCategory(cat); setStatusFilter('all'); setFirmFilter('all'); setShowCreate(false); },
      };
    }).filter(Boolean);
  }, [savedCategoryOrder, activeCategory, searchCount, projectCount, allData.length]);

  const handleSidebarReorder = useCallback((newItems) => {
    setSavedCategoryOrder(newItems.map(i => i.key));
  }, [setSavedCategoryOrder]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  // Create project handler
  const handleCreateProject = async () => {
    if (!newProject.name.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/pipelines/create-project`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newProject),
      });
      if (res.ok) {
        const project = await res.json();
        setAllData(prev => [{ ...project, category: 'project' }, ...prev]);
        setShowCreate(false);
        setNewProject({ name: '', firm_id: '', description: '', location: '' });
        setActiveCategory('project');
      }
    } catch (err) {
      console.error('Error creating project:', err);
    } finally {
      setCreating(false);
    }
  };

  // Create executive search handler
  const handleCreateSearch = async () => {
    if (!newSearch.name.trim() || !newSearch.client_firm_id) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/pipelines/create-search`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newSearch),
      });
      if (res.ok) {
        const search = await res.json();
        setAllData(prev => [{ ...search, category: 'executive_search' }, ...prev]);
        setShowCreate(false);
        setNewSearch({ name: '', client_firm_id: '', description: '', requirements: '', location: '', compensation_range: '' });
        setActiveCategory('executive_search');
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to create search');
      }
    } catch (err) {
      console.error('Error creating search:', err);
    } finally {
      setCreating(false);
    }
  };

  // Status change handler
  const handleStatusChange = async (e, pipelineId, newStatus) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/api/pipelines/${pipelineId}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setAllData(prev => prev.map(p => p.id === pipelineId ? { ...p, status: newStatus } : p));
      }
    } catch (err) { console.error(err); }
  };

  // Delete handler
  const handleToggleAssign = async (pipelineId, userId) => {
    const pipeline = allData.find(p => p.id === pipelineId);
    const current = (pipeline?.assigned_users || []).map(u => u.user_id);
    const newIds = current.includes(userId) ? current.filter(id => id !== userId) : [...current, userId];
    try {
      await fetch(`${API}/api/user-management/search-assignments/${pipelineId}`, {
        method: 'PUT', headers, body: JSON.stringify({ userIds: newIds })
      });
      // Update local state
      const newUsers = newIds.map(id => {
        const tm = teamMembers.find(t => t.id === id);
        return tm ? { user_id: id, user_name: tm.name, avatar_url: tm.avatar_url } : { user_id: id, user_name: '?', avatar_url: null };
      });
      setAllData(prev => prev.map(p => p.id === pipelineId ? { ...p, assigned_users: newUsers } : p));
    } catch (err) { console.error('Error assigning user:', err); }
  };


  // ── PAGE STRIP ────────────────────────────────────────────────────────
  // Horizontal category pills replace the old DraggableMiniSidebar. Order
  // still persists per the user's savedCategoryOrder — drag reorder can
  // return later as horizontal drag if we want it, but pills at this count
  // (two of them) don't need reordering.
  const tabStrip = (
    <div className="bg-white px-3 h-[40px] flex items-center">
      <nav className="flex gap-1 h-[40px] -my-px items-stretch flex-shrink-0">
        {sidebarItems.map((item) => (
          <button
            key={item.key}
            onClick={item.onClick}
            className={`flex-shrink-0 px-3 text-xs font-medium border-b-2 rounded-none transition-colors flex items-center gap-1.5 ${
              item.active
                ? 'border-emerald-600 text-emerald-700 font-semibold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              {item.icon}
            </span>
            {item.title}
            {item.badge != null && item.badge > 0 && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                  item.active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );

  // Bulk bar replaces the tab strip in place when searches are selected.
  const pageStrip = (selectedIds.size > 0 && activeCategory !== 'opportunities') ? (
    <BulkActionBar
      count={selectedIds.size}
      busy={bulkUpdating}
      onClear={clearSelection}
      actions={[
        { label: 'Delete', onClick: bulkDelete },
        { label: 'Export CSV', onClick: () => exportSearchesToCSV(filtered.filter(p => selectedIds.has(p.id)), activeCategory) },
      ]}
    >
      <select
        value=""
        disabled={bulkUpdating}
        onChange={(e) => e.target.value && handleBulkStatusChange(e.target.value)}
        className="text-xs border border-white/30 bg-white/10 text-white rounded px-2 h-[26px] focus:outline-none focus:ring-1 focus:ring-white/40 disabled:opacity-50 cursor-pointer"
      >
        <option value="" disabled className="text-gray-800">Status</option>
        {STATUS_OPTIONS.filter(o => o.value !== 'all').map(o => (
          <option key={o.value} value={o.value} className="text-gray-800">{o.label}</option>
        ))}
      </select>
      {bulkUpdating && <span className="text-[10px] text-white/80">Updating…</span>}
    </BulkActionBar>
  ) : tabStrip;

  return (
    <AppShell
      pageStrip={pageStrip}
      rightPanel={editPipeline ? (
        <EditSearchDrawer
          pipeline={editPipeline}
          onClose={() => setEditPipeline(null)}
          onSaved={(updated) => {
            setAllData(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
            setEditPipeline(null);
          }}
        />
      ) : null}
    >
      <div className="h-full flex overflow-hidden">
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden h-full min-h-0">
          {/* Opportunities category takes over the whole main area with
              its own section. Everything below (toolbar + table + modals)
              only renders for searches/projects/all. */}
          {activeCategory === 'opportunities' ? (
            <div className="flex-1 overflow-y-auto">
              <OpportunityRolesSection />
            </div>
          ) : (
          <>
          {/* Toolbar */}
          <div className="bg-[color:var(--list-page-bg)] px-3 pt-2 pb-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  placeholder="Search..."
                  className="h-[30px] w-44 pl-7 pr-2 text-sm border border-gray-300 rounded focus:outline-none transition-shadow"
                  style={searchFocused ? { boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.15), 0 0 14px 2px rgba(16, 185, 129, 0.22), 0 0 28px 3px rgba(16, 185, 129, 0.10)', borderColor: 'rgba(16, 185, 129, 0.4)' } : undefined}
                />
                <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Status filter */}
              <FilterSelect
                label="Status"
                value={statusFilter}
                defaultValue="all"
                onChange={setStatusFilter}
                options={STATUS_OPTIONS}
              />

              {/* Firm filter */}
              <FilterSelect
                label="Company"
                value={firmFilter}
                defaultValue="all"
                onChange={setFirmFilter}
                minWidth={140}
                options={[{ value: 'all', label: 'All Companies' }, ...availableFirms.map(([id, name]) => ({ value: id, label: name }))]}
              />

              {/* Sort */}
              <FilterSelect
                label="Sort"
                value={sortBy}
                defaultValue="created_at"
                onChange={setSortBy}
                options={[
                  { value: 'created_at', label: 'Newest First' },
                  { value: 'name', label: 'Name A-Z' },
                  { value: 'firm_name', label: 'Company A-Z' },
                  { value: 'card_count', label: 'Most Candidates' },
                ]}
              />

              {/* View mode toggle — table (rows) vs. cards (grid).
                  Persists via localStorage. Hidden on Opportunities
                  which renders its own layout. */}
              {activeCategory !== 'opportunities' && (
                <div className="h-[30px] flex items-center gap-0.5 border border-gray-300 rounded p-0.5" role="tablist" aria-label="View mode">
                  <button
                    onClick={() => setViewMode('table')}
                    aria-label="Table view"
                    title="Table view"
                    className={`h-full px-1.5 flex items-center rounded transition-colors ${
                      viewMode === 'table'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('cards')}
                    aria-label="Card view"
                    title="Card view"
                    className={`h-full px-1.5 flex items-center rounded transition-colors ${
                      viewMode === 'cards'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                </div>
              )}

              <div className="flex-1" />

              {/* Column visibility selector — sits just left of the create button */}
              {activeCategory !== 'opportunities' && viewMode === 'table' && (
                <div className="relative">
                  <button onClick={() => setShowColMenu(v => !v)} title="Show/hide columns"
                    className="h-[30px] px-1.5 border border-gray-300 rounded text-gray-500 hover:bg-gray-50 flex items-center gap-0.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    <svg className={`w-3 h-3 transition-transform ${showColMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {showColMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowColMenu(false)} />
                      <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                        <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Columns</div>
                        {COLUMNS.map(col => (
                          <button key={col.key} onClick={() => toggleCol(col.key)}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-emerald-50 transition-colors">
                            <input type="checkbox" readOnly checked={!!visibleCols[col.key]}
                              className="w-4 h-4 rounded border-gray-300 accent-emerald-600 pointer-events-none" />
                            {col.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Create — single button opens a combined sidebar with a type picker */}
              {hasPermission('create_searches') && activeCategory !== 'opportunities' && (
                <button
                  onClick={() => { setShowCreate(true); setCreateType(activeCategory === 'project' ? 'project' : activeCategory === 'executive_search' ? 'search' : null); }}
                  className="h-[30px] px-3 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add
                </button>
              )}
            </div>
          </div>

          {/* Bulk action bar — visible whenever 1+ searches are selected.
              Sits below the toolbar, above the results. Emerald tint so
              it reads as an active selection state, matches other emerald
              accents in the shell. */}
          {/* Table */}
          <div className="flex-1 min-h-0 flex flex-col bg-[color:var(--list-page-bg)]">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={
                    activeCategory === 'project'
                      ? "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                      : "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  } />
                </svg>
                <p className="text-gray-500 text-sm">
                  {activeCategory === 'project' ? 'No projects yet' : 'No executive searches found'}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {activeCategory === 'project'
                    ? 'Create a project to start tracking candidates'
                    : 'Create a search or have clients submit through the partnership system'}
                </p>
                {activeCategory === 'project' && hasPermission('create_searches') && (
                  <button
                    onClick={() => { setShowCreate(true); setCreateType('project'); }}
                    className="mt-4 px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700"
                  >
                    + New Project
                  </button>
                )}
                {activeCategory === 'executive_search' && hasPermission('create_searches') && (
                  <button
                    onClick={() => { setShowCreate(true); setCreateType('search'); }}
                    className="mt-4 px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700"
                  >
                    + New Executive Search
                  </button>
                )}
              </div>
            ) : viewMode === 'cards' ? (
              /* Card grid — responsive columns. Cards click through to
                 /search/{id}. Actions like status change / assign are
                 available on the detail page, keeping cards clean. */
              <div className="flex-1 min-h-0 overflow-auto px-3 pb-2">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 py-3">
                {filtered.map((p, idx) => {
                  const count = parseInt(p.card_count) || 0;
                  const code = `SR-${String(p.id).padStart(4, '0')}`;
                  return (
                    <div
                      key={p.id}
                      data-context-type="search"
                      data-context-id={p.id}
                      className={`group bg-white border rounded-lg p-4 hover:shadow-md transition-all flex flex-col gap-3 ${selectedIds.has(p.id) ? 'border-emerald-400 ring-1 ring-emerald-300' : 'border-gray-200'}`}
                    >
                      {/* Header row: checkbox + name + status */}
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(p.id)}
                          onChange={(e) => toggleWithShift(p.id, idx, filtered, e.nativeEvent.shiftKey)}
                          className="w-4 h-4 mt-0.5 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 cursor-pointer flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div onClick={() => navigate(`/search/${p.id}`)} className="text-sm font-semibold text-gray-900 truncate cursor-pointer hover:text-emerald-700 hover:underline" title={p.name}>{p.name}</div>
                          <div className="text-[10px] text-gray-400 font-mono mt-0.5">{code}</div>
                        </div>
                        <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${statusStyle(p.status)}`}>
                          {(STATUS_OPTIONS.find(o => o.value === p.status)?.label) || p.status}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditPipeline(p); }}
                          title="Edit search"
                          className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all opacity-0 group-hover:opacity-100"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                      </div>

                      {/* Client + location */}
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span className="truncate" title={p.firm_name}>{p.firm_name || '—'}</span>
                        </div>
                        {p.location && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="truncate">{p.location}</span>
                          </div>
                        )}
                      </div>

                      {/* Bottom row: candidate count + assigned users */}
                      <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${count > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {count}
                          </span>
                          {p.created_by_name && (
                            <span className="text-[10px] text-gray-400" title={`Created by ${p.created_by_name}`}>· {p.created_by_name}</span>
                          )}
                        </div>
                        {(p.assigned_users || []).length > 0 && (
                          <div className="flex -space-x-1.5">
                            {(p.assigned_users || []).slice(0, 3).map(u => (
                              u.avatar_url ? (
                                <img key={u.user_id} src={u.avatar_url} alt="" className="w-5 h-5 rounded-full border-2 border-white object-cover" title={u.user_name} />
                              ) : (
                                <div key={u.user_id} className="w-5 h-5 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-white text-[8px] font-bold" title={u.user_name}>
                                  {u.user_name?.charAt(0) || '?'}
                                </div>
                              )
                            ))}
                            {(p.assigned_users || []).length > 3 && (
                              <div className="w-5 h-5 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-[8px] text-gray-600 font-medium">
                                +{p.assigned_users.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              </div>
            ) : (
              <div className="flex-1 min-h-0 mx-3 mb-1 bg-[color:var(--list-block-bg)] border rounded-lg shadow-sm overflow-hidden flex flex-col">
              <table className="w-full text-sm border-separate border-spacing-0 flex flex-col min-h-0 flex-1" style={{ tableLayout: 'fixed' }}>
                <thead className="flex-shrink-0" style={{ display: 'table', width: '100%', tableLayout: 'fixed', backgroundColor: 'var(--list-header-bg)', color: 'var(--list-header-text)' }}>
                  <tr>
                    <th style={{borderBottom:'1px solid var(--list-header-border)', backgroundColor:'var(--list-header-bg)'}} className="w-10 pl-4 pr-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={filtered.length > 0 && filtered.every(p => selectedIds.has(p.id))}
                        onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 cursor-pointer"
                        title="Select all visible"
                      />
                    </th>
                    <th style={{borderBottom:'1px solid var(--list-header-border)', backgroundColor:'var(--list-header-bg)'}} className="px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider">
                      <button onClick={() => toggleSort('name')} className="flex items-center gap-0.5 uppercase hover:opacity-80">
                        {activeCategory === 'executive_search' ? 'Search' : 'Project'}
                        <svg className={`w-3 h-3 ${sortBy === 'name' ? 'opacity-100' : 'opacity-25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">{sortBy === 'name' && sortDir === 'asc' ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />}</svg>
                      </button>
                    </th>
                    {visibleCols.firm_name && (
                    <th style={{borderBottom:'1px solid var(--list-header-border)', backgroundColor:'var(--list-header-bg)'}} className="px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider"><button onClick={() => toggleSort('firm_name')} className="flex items-center gap-0.5 uppercase hover:opacity-80">Company<svg className={`w-3 h-3 ${sortBy === 'firm_name' ? 'opacity-100' : 'opacity-25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">{sortBy === 'firm_name' && sortDir === 'asc' ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />}</svg></button></th>
                    )}
                    {visibleCols.location && (
                    <th style={{borderBottom:'1px solid var(--list-header-border)', backgroundColor:'var(--list-header-bg)'}} className="px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider">Location</th>
                    )}
                    {visibleCols.card_count && (
                    <th style={{borderBottom:'1px solid var(--list-header-border)', backgroundColor:'var(--list-header-bg)'}} className="px-4 py-2 text-center text-[10px] font-medium uppercase tracking-wider"><button onClick={() => toggleSort('card_count')} className="inline-flex items-center gap-0.5 uppercase hover:opacity-80">Candidates<svg className={`w-3 h-3 ${sortBy === 'card_count' ? 'opacity-100' : 'opacity-25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">{sortBy === 'card_count' && sortDir === 'asc' ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />}</svg></button></th>
                    )}
                    {visibleCols.status && (
                    <th style={{borderBottom:'1px solid var(--list-header-border)', backgroundColor:'var(--list-header-bg)'}} className="px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider">Status</th>
                    )}
                    {visibleCols.created_by && (
                    <th style={{borderBottom:'1px solid var(--list-header-border)', backgroundColor:'var(--list-header-bg)'}} className="px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider">Created By</th>
                    )}
                    {visibleCols.assigned && (
                    <th style={{borderBottom:'1px solid var(--list-header-border)', backgroundColor:'var(--list-header-bg)'}} className="px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider">Assigned</th>
                    )}
                    {visibleCols.created_at && (
                    <th style={{borderBottom:'1px solid var(--list-header-border)', backgroundColor:'var(--list-header-bg)'}} className="px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider"><button onClick={() => toggleSort('created_at')} className="flex items-center gap-0.5 uppercase hover:opacity-80">Created<svg className={`w-3 h-3 ${sortBy === 'created_at' ? 'opacity-100' : 'opacity-25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">{sortBy === 'created_at' && sortDir === 'asc' ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />}</svg></button></th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 flex-1 min-h-0 overflow-y-auto" style={{ display: 'block', scrollbarGutter: 'stable' }}>
                  {filtered.map((p, idx) => {
                    const count = parseInt(p.card_count) || 0;
                    const code = `SR-${String(p.id).padStart(4, '0')}`;
                    return (
                      <tr
                        key={p.id}
                        data-context-type="search"
                        data-context-id={p.id}
                        style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}
                        className={`group transition-colors ${selectedIds.has(p.id) ? 'bg-emerald-100/60' : 'bg-white/60 hover:bg-emerald-50/50'}`}
                      >
                        <td className="w-10 pl-4 pr-2 py-2.5 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(p.id)}
                            onChange={(e) => toggleWithShift(p.id, idx, filtered, e.nativeEvent.shiftKey)}
                            className="w-4 h-4 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <div>
                            <div onClick={() => navigate(`/search/${p.id}`)} className="text-sm font-medium text-gray-900 cursor-pointer hover:text-emerald-700 hover:underline inline-block">{p.name}</div>
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-gray-400 font-mono">{code}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditPipeline(p); }}
                                title="Edit search"
                                className="flex-shrink-0 p-0.5 rounded text-gray-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all opacity-0 group-hover:opacity-100"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </button>
                            </div>
                          </div>
                        </td>
                        {visibleCols.firm_name && (
                        <td className="px-4 py-2.5">
                          <span className="text-sm text-gray-600">{p.firm_name || '—'}</span>
                        </td>
                        )}
                        {visibleCols.location && (
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-gray-500">{p.location || '—'}</span>
                        </td>
                        )}
                        {visibleCols.card_count && (
                        <td className="px-4 py-2.5 text-center">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            count > 0 ? 'bg-blue-100 text-blue-700' : 'text-gray-400'
                          }`}>
                            {count}
                          </span>
                        </td>
                        )}
                        {visibleCols.status && (
                        <td className="px-4 py-2.5">
                          <select
                            value={p.status}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleStatusChange(e, p.id, e.target.value)}
                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium border-0 cursor-pointer ${statusStyle(p.status)}`}
                          >
                            {STATUS_OPTIONS.filter(o => o.value !== 'all').map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </td>
                        )}
                        {visibleCols.created_by && (
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-gray-500">{p.created_by_name || '—'}</span>
                        </td>
                        )}
                        {visibleCols.assigned && (
                        <td className="px-4 py-2.5">
                          <div className="relative">
                            <button onClick={(e) => { e.stopPropagation(); setAssignPopover(assignPopover === p.id ? null : p.id); }}
                              className="flex items-center gap-1 hover:bg-blue-50 rounded px-1.5 py-0.5 transition-colors">
                              {(p.assigned_users || []).length > 0 ? (
                                <div className="flex -space-x-1.5">
                                  {(p.assigned_users || []).slice(0, 4).map(u => (
                                    u.avatar_url ? (
                                      <img key={u.user_id} src={u.avatar_url} alt="" className="w-5 h-5 rounded-full border-2 border-white object-cover" title={u.user_name} />
                                    ) : (
                                      <div key={u.user_id} className="w-5 h-5 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-white text-[8px] font-bold" title={u.user_name}>
                                        {u.user_name?.charAt(0) || '?'}
                                      </div>
                                    )
                                  ))}
                                  {(p.assigned_users || []).length > 4 && <div className="w-5 h-5 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-[8px] text-gray-600 font-medium">+{p.assigned_users.length - 4}</div>}
                                </div>
                              ) : (
                                <span className="text-[10px] text-gray-300">+ Assign</span>
                              )}
                            </button>
                            {assignPopover === p.id && (
                              <>
                                <div className="fixed inset-0 z-20" onClick={(e) => { e.stopPropagation(); setAssignPopover(null); }} />
                                <div className="absolute top-full left-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1 max-h-56 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                  <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Assign Team Members</p>
                                  {teamMembers.map(tm => {
                                    const isAssigned = (p.assigned_users || []).some(u => u.user_id === tm.id);
                                    return (
                                      <button key={tm.id} onClick={() => handleToggleAssign(p.id, tm.id)}
                                        className={`w-full px-3 py-1.5 flex items-center gap-2 hover:bg-blue-50 text-left ${isAssigned ? 'bg-blue-50/50' : ''}`}>
                                        {tm.avatar_url ? (
                                          <img src={tm.avatar_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                                        ) : (
                                          <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-[8px] font-bold">{tm.name?.charAt(0)}</div>
                                        )}
                                        <span className="text-xs text-gray-800 flex-1">{tm.name}</span>
                                        {isAssigned && (
                                          <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        )}
                                      </button>
                                    );
                                  })}
                                  {teamMembers.length === 0 && <p className="px-3 py-2 text-[10px] text-gray-400">No team members found</p>}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                        )}
                        {visibleCols.created_at && (
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-gray-500">{formatDate(p.submitted_at || p.created_at)}</span>
                        </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
            {/* Count footer — bottom-right below the grid */}
            {activeCategory !== 'opportunities' && (
              <div className="flex-shrink-0 px-4 py-1.5 flex justify-end">
                <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">
                  <span className="font-medium text-gray-500">{filtered.length}</span>
                  {' '}{activeCategory === 'executive_search' ? 'searches' : 'projects'}
                  <span className="mx-1 text-gray-300">/</span>
                  <span className="font-medium text-gray-500">{totalCandidates}</span> candidates
                </span>
              </div>
            )}
          </div>
          </>
          )}
        </div>

      {/* Create — combined right sidebar with a type picker */}
      {showCreate && (
        <div className="flex-shrink-0 w-[360px] bg-white border-l border-gray-200 flex flex-col animate-[slidein_180ms_ease-out] overflow-hidden">
          <style>{`@keyframes slidein { from { opacity: 0; transform: translateX(20px);} to { opacity: 1; transform: translateX(0);} }`}</style>
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {createType && (
                <button onClick={() => setCreateType(null)} className="p-1 -ml-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100" title="Back">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
              )}
              <h3 className="font-semibold text-gray-900">
                {createType === 'search' ? 'New Executive Search' : createType === 'project' ? 'New Project' : 'Add New'}
              </h3>
            </div>
            <button onClick={() => setShowCreate(false)} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Step 1: type picker */}
          {!createType && (
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <p className="text-xs text-gray-500">What would you like to create?</p>
              <button onClick={() => setCreateType('search')}
                className="w-full flex items-start gap-3 p-3 border border-gray-200 rounded-lg text-left hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors">
                <span className="mt-0.5 w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">Executive Search</span>
                  <span className="block text-[11px] text-gray-500">A client-facing search. The client firm is notified.</span>
                </span>
              </button>
              <button onClick={() => setCreateType('project')}
                className="w-full flex items-start gap-3 p-3 border border-gray-200 rounded-lg text-left hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors">
                <span className="mt-0.5 w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">Project</span>
                  <span className="block text-[11px] text-gray-500">An internal project, optionally tied to a company.</span>
                </span>
              </button>
            </div>
          )}

          {/* Step 2a: Executive Search form */}
          {createType === 'search' && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Search / Position Name *</label>
                  <input type="text" value={newSearch.name} onChange={(e) => setNewSearch(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Chief Financial Officer"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500" autoFocus />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Client Company *</label>
                  <select value={newSearch.client_firm_id} onChange={(e) => setNewSearch(prev => ({ ...prev, client_firm_id: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500">
                    <option value="">Select a client company...</option>
                    {firms.map(f => (<option key={f.id} value={f.id}>{f.name}</option>))}
                  </select>
                  <p className="text-[10px] text-gray-400 mt-1">The client firm will be notified about this search</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                    <input type="text" value={newSearch.location} onChange={(e) => setNewSearch(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g., San Francisco, CA"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Compensation Range</label>
                    <input type="text" value={newSearch.compensation_range} onChange={(e) => setNewSearch(prev => ({ ...prev, compensation_range: e.target.value }))}
                      placeholder="e.g., $200K-$300K"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={newSearch.description} onChange={(e) => setNewSearch(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Overview of the role and what the client is looking for..." rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500 resize-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Requirements</label>
                  <textarea value={newSearch.requirements} onChange={(e) => setNewSearch(prev => ({ ...prev, requirements: e.target.value }))}
                    placeholder="Must-haves, nice-to-haves, years of experience..." rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500 resize-none" />
                </div>
              </div>
              <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreateSearch} disabled={!newSearch.name.trim() || !newSearch.client_firm_id || creating}
                  className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create & Notify Client'}
                </button>
              </div>
            </>
          )}

          {/* Step 2b: Project form */}
          {createType === 'project' && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Project Name *</label>
                  <input type="text" value={newProject.name} onChange={(e) => setNewProject(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., VP Engineering — Fintech Sector"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500" autoFocus />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Client Company (optional)</label>
                  <select value={newProject.firm_id} onChange={(e) => setNewProject(prev => ({ ...prev, firm_id: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500">
                    <option value="">No company</option>
                    {firms.map(f => (<option key={f.id} value={f.id}>{f.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Location (optional)</label>
                  <input type="text" value={newProject.location} onChange={(e) => setNewProject(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g., New York, NY"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
                  <textarea value={newProject.description} onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the project..." rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500 resize-none" />
                </div>
              </div>
              <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreateProject} disabled={!newProject.name.trim() || creating}
                  className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      </div>{/* end outer flex container */}
    </AppShell>
  );
};

export default SearchesPage;
